'use client';

import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { UploadSimpleIcon, XIcon } from '@phosphor-icons/react';
import { Button } from '@tranquility-ai/theme-components/button';
import { recordStat } from '@/shared/functions';
import { StatsContext } from '@/app/contexts';
import {
  navBarButtonProps,
  navBarMenuPanelItemClassName,
  navBarVerticalButtonClassName,
  navBarVerticalIconClassName,
  navBarVerticalStackClassName,
} from './nav-bar-button';
import { primaryNavActionClassName } from './primary-navigation/primary-nav-action';
import { isAdHocCase } from '@/shared/ad-hoc-evidence';
import { usePersonalFilesCaseId } from '@/hooks/usePersonalFilesCaseId';
import { resolveCellAppDomainSuffix } from '@/shared/cell-session';
import { assertAllowedCellId } from '@/lib/safe-outbound-url';
import { getCachedAccessToken } from '@/lib/gateway-fetch';
import type { AdHocCaseFileUploadApi } from '@/hooks/useAdHocCaseFileUpload';
import { useTimePilotT } from '@/i18n/useTimePilotT';
import { presignedIntegrationPath } from '@/shared/integration-presigned-param';
import { requestIntegrationPresignedUrl } from '@/lib/integration-presigned-url';
import {
  externalIntegrationHandoffOrigin,
  INTEGRATION_HANDOFF_ACTION_FIELD,
  INTEGRATION_HANDOFF_PATH,
  integrationFormActionAllowed,
} from '@/lib/integration-form-action';

type IntegrationParamValue = string | true;

type IntegrationConfig = {
  name: string;
  baseUrl?: string;
  path?: string;
  params?: Record<string, IntegrationParamValue>;
};

const isValidIntegrationName = (name: unknown): name is string =>
  typeof name === 'string' && name.trim() !== '';

const isPageOriginBaseUrl = (baseUrl: string): boolean => {
  const trimmed = baseUrl.trim();
  return trimmed === '' || trimmed === '/';
};

const isValidIntegrationBaseUrl = (baseUrl: unknown): baseUrl is string => {
  if (typeof baseUrl !== 'string') return false;
  const trimmed = baseUrl.trim();
  if (!trimmed || isPageOriginBaseUrl(trimmed)) return false;
  try {
    const url = new URL(trimmed);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

/**
 * Origin for integrations configured with a page-origin ``baseUrl`` (``"/"`` or
 * ``""``). Prefer the caller's cell **frontend** host
 * (``https://{cellId}.timepilot.tranquility.ai``) because that host both routes
 * to the per-cell ALB path rules (``/evidence-com/*``, ``/fivecast/*``, …) and
 * is forward-only at the edge — so a cross-origin ``<form target="_blank">``
 * POST reaches the integration app, which then runs its own login. The ``.api``
 * host is NOT usable here: it sits behind the edge API-Gateway authorizer, which
 * 401s a header-less browser POST.
 *
 * Falling back to ``window.location.origin`` only works when the frontend is
 * co-located with the cell ALB (dev/staging, where the browser is already on the
 * cell host) — in prod the shared origin (``timepilot.tranquility.ai``) has no
 * such rules, so the POST would land back on the SPA. Returns ``null`` when no
 * cell is known and no page origin is available.
 *
 * The cell id comes from the ``custom:cell_id`` JWT claim via an unsigned
 * decode, so it is validated with ``assertAllowedCellId`` before interpolation —
 * a malformed claim (e.g. ``evil.com/``) would otherwise yield a foreign origin
 * (``https://evil.com/.timepilot.tranquility.ai`` → host ``evil.com``), and the
 * integration submit is a cross-origin POST carrying tenant/case/user with no
 * same-origin policy to stop it. Mirrors the guard every other cell URL goes
 * through (``buildCellServiceUrl``).
 */
const resolveCellFrontendOrigin = (cellId?: string, pageOrigin?: string): string | null => {
  const trimmed = cellId?.trim();
  if (trimmed) {
    try {
      assertAllowedCellId(trimmed);
    } catch {
      return pageOrigin ?? null; // malformed claim: never build a foreign origin
    }
    return `https://${trimmed}.${resolveCellAppDomainSuffix()}`;
  }
  return pageOrigin ?? null;
};

const resolveIntegrationBase = (
  baseUrl: string | undefined,
  cellId?: string,
  pageOrigin?: string
): string | null => {
  if (baseUrl === undefined) return resolveCellFrontendOrigin(cellId, pageOrigin);
  if (isPageOriginBaseUrl(baseUrl)) return resolveCellFrontendOrigin(cellId, pageOrigin);
  return baseUrl;
};

const isIntegrationEntry = (item: unknown): item is Record<string, unknown> =>
  item !== null && item !== undefined && typeof item === 'object' && !Array.isArray(item);

const INTEGRATION_PATH_RESOLVE_BASE = 'https://placeholder.invalid/';

/** Reject absolute and protocol-relative paths that would ignore the configured base URL. */
const isValidIntegrationPath = (path: unknown): path is string => {
  if (typeof path !== 'string') return false;
  const trimmed = path.trim();
  if (!trimmed || trimmed.startsWith('//')) return false;
  try {
    const resolved = new URL(trimmed, INTEGRATION_PATH_RESOLVE_BASE);
    return resolved.origin === 'https://placeholder.invalid';
  } catch {
    return false;
  }
};

const getIntegrationPath = (raw: Record<string, unknown>): string | undefined => {
  const path = raw.path ?? raw.pathname;
  if (!isValidIntegrationPath(path)) return undefined;
  return path.trim();
};

const STANDARD_POST_FIELDS = new Set(['tenant', 'case', 'user', 'auth_token']);

const readParamValue = (key: string, value: unknown): IntegrationParamValue | undefined => {
  if (value === true && STANDARD_POST_FIELDS.has(key)) return true;
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
};

const readParamRecord = (raw: unknown): Record<string, IntegrationParamValue> | undefined => {
  if (!isIntegrationEntry(raw)) return undefined;
  const out: Record<string, IntegrationParamValue> = {};
  for (const [key, value] of Object.entries(raw)) {
    const trimmedKey = key.trim();
    if (!trimmedKey) continue;
    const parsed = readParamValue(trimmedKey, value);
    if (parsed === undefined) continue;
    out[trimmedKey] = parsed;
  }
  return Object.keys(out).length > 0 ? out : undefined;
};

/** Flag `params` is an object, or an array of objects, of strings and opt-in booleans. */
const readIntegrationParams = (raw: unknown): Record<string, IntegrationParamValue> | undefined => {
  if (Array.isArray(raw)) {
    const out: Record<string, IntegrationParamValue> = {};
    for (const item of raw) {
      const record = readParamRecord(item);
      if (!record) continue;
      Object.assign(out, record);
    }
    return Object.keys(out).length > 0 ? out : undefined;
  }
  return readParamRecord(raw);
};

const parseIntegrations = (
  integrations: unknown
): Array<{ key: string; config: IntegrationConfig }> => {
  if (!Array.isArray(integrations)) return [];

  return integrations.flatMap((item) => {
    if (!isIntegrationEntry(item)) return [];

    return Object.entries(item).flatMap(([key, rawConfig]) => {
      if (!isIntegrationEntry(rawConfig)) return [];

      if (!isValidIntegrationName(rawConfig.name)) return [];

      const hasBaseUrl = Object.prototype.hasOwnProperty.call(rawConfig, 'baseUrl');
      let baseUrl: string | undefined;
      if (hasBaseUrl && rawConfig.baseUrl != null) {
        const baseUrlRaw = String(rawConfig.baseUrl).trim();
        if (!isPageOriginBaseUrl(baseUrlRaw)) {
          if (!isValidIntegrationBaseUrl(rawConfig.baseUrl)) return [];
          baseUrl = baseUrlRaw;
        }
      }

      const path = getIntegrationPath(rawConfig);
      const hasParams = Object.prototype.hasOwnProperty.call(rawConfig, 'params');
      const params = readIntegrationParams(rawConfig.params);

      return [{
        key,
        config: {
          name: String(rawConfig.name).trim(),
          ...(baseUrl ? { baseUrl } : {}),
          ...(path ? { path } : {}),
          ...(hasParams ? { params: params ?? {} } : {}),
        },
      }];
    });
  });
};

type IntegrationPostPayload = {
  tenant: string;
  case: string;
  user: string;
};

const buildIntegrationPostTarget = (
  config: IntegrationConfig,
  tenantId: string,
  caseId: string,
  username: string,
  cellId?: string,
  pageOrigin?: string
): { action: string; payload: IntegrationPostPayload } | null => {
  const base = resolveIntegrationBase(config.baseUrl, cellId, pageOrigin);
  if (!base) return null;

  try {
    const baseUrl = new URL(base);
    if (config.path && !isValidIntegrationPath(config.path)) return null;
    const url = config.path ? new URL(config.path, baseUrl) : new URL(baseUrl);
    if (url.origin !== baseUrl.origin) return null;
    if (config.path && !url.pathname.endsWith('/')) {
      url.pathname = `${url.pathname}/`;
    }
    return {
      action: url.toString(),
      payload: {
        tenant: tenantId,
        case: caseId,
        user: username,
      },
    };
  } catch {
    return null;
  }
};

/**
 * Whether `action` is one of our own origins, and therefore safe to hand an
 * access token to.
 *
 * This gate is not optional. An integration's `baseUrl` comes from tenant
 * feature-flag config and `isValidIntegrationBaseUrl` accepts ANY absolute
 * http(s) URL, so a configured integration can legitimately point at an external
 * vendor. Attaching the caller's token unconditionally would hand a live Cognito
 * bearer credential to that third party — on every click, for every tenant whose
 * config names an outside host.
 *
 * Two origins qualify:
 *  - a cell frontend host under the partition's app domain, over https — the
 *    normal production target, where the integration app is ours; and
 *  - this page's own origin, which covers local development (`http://localhost`)
 *    and the dev/staging cells where the browser already sits on the cell host.
 *
 * Anything else gets the POST without a token, exactly as before this change.
 * `assertAllowedCellId` already stops a malformed `custom:cell_id` claim from
 * fabricating a cell origin (see resolveCellFrontendOrigin), so this is the
 * second half of the same guard: that one controls where we POST, this one
 * controls what we attach.
 */
const isOwnOrigin = (action: string): boolean => {
  try {
    const url = new URL(action);
    if (typeof window !== 'undefined' && url.origin === window.location.origin) return true;
    return url.protocol === 'https:' && url.host.endsWith(`.${resolveCellAppDomainSuffix()}`);
  } catch {
    return false;
  }
};

/**
 * The value normally posted for tenant, case, user, or auth_token.
 * `auth_token` is omitted off our own origins so a flag cannot forward the
 * bearer token to an external host.
 */
const standardFieldValue = (
  name: string,
  action: string,
  payload: IntegrationPostPayload,
): string | undefined => {
  if (name === 'tenant') return payload.tenant;
  if (name === 'case') return payload.case;
  if (name === 'user') return payload.user;
  if (name === 'auth_token') {
    if (!isOwnOrigin(action)) return undefined;
    return getCachedAccessToken() ?? '';
  }
  return undefined;
};

/**
 * Fields from a flag `params` element. These replace the standard handoff.
 * `tenant` / `case` / `user` / `auth_token` are sent only when the flag sets
 * that key to boolean true. A string value that is exactly one of those names
 * is replaced with the real value and posted under the param's own name.
 */
const flagParamFields = (
  params: Record<string, IntegrationParamValue> | undefined,
  action: string,
  payload: IntegrationPostPayload,
): Record<string, string> => {
  const fields: Record<string, string> = {};
  if (!params) return fields;
  for (const [key, value] of Object.entries(params)) {
    if (value === true) {
      const resolved = standardFieldValue(key, action, payload);
      if (resolved !== undefined) fields[key] = resolved;
      continue;
    }
    if (STANDARD_POST_FIELDS.has(value)) {
      const resolved = standardFieldValue(value, action, payload);
      if (resolved !== undefined) fields[key] = resolved;
      continue;
    }
    if (STANDARD_POST_FIELDS.has(key)) continue;
    fields[key] = value;
  }
  return fields;
};

/** Fields the integration form will submit, including `auth_token` on our own origins. */
const integrationPostFields = (
  action: string,
  payload: Record<string, string>,
): Record<string, string> => {
  const fields: Record<string, string> = {};
  for (const [name, value] of Object.entries(payload)) {
    if (name === 'auth_token') continue;
    fields[name] = value;
  }
  if (isOwnOrigin(action)) {
    fields.auth_token = getCachedAccessToken() ?? '';
  }
  return fields;
};

const buildIntegrationForm = (
  doc: Document,
  action: string,
  fields: Record<string, string>,
  target?: string,
): HTMLFormElement => {
  const form = doc.createElement('form');
  form.method = 'POST';
  form.action = action;
  if (target) form.target = target;

  // The integration app has no way to recognise this user on its own: the POST is
  // cross-origin (shared app origin → cell host), so none of our cookies reach it.
  // Until now that left `tenant`/`case`/`user` below as the only identity on the
  // request — plain form fields, trusted on arrival, which is what made every
  // integration route reachable by anyone who could reproduce the POST.
  //
  // Send the access token so the receiving app can verify a signature instead of
  // trusting a string, and resolve the tenant from a claim rather than from the
  // body. The token names its own pool in `iss`, so one check covers both the
  // customer and employee pools without the app having to guess which the caller
  // belongs to.
  //
  // `auth_token` is attached in integrationPostFields, which reads the cache at
  // the moment the fields are built (the click, or when the presigned URL
  // returns). Synchronous on purpose — see getCachedAccessToken.
  //
  // A null token still submits. The integration app answers with its own
  // "open this from TimePilot" page, which is a clearer outcome than silently
  // doing nothing when a user clicks; and it cannot be a false negative for a
  // signed-in user, because the shell keeps this cache warm.
  //
  // Only ever to our own origin — see isOwnOrigin. An integration configured to
  // point at an external vendor must not receive a bearer token.
  for (const [name, value] of Object.entries(fields)) {
    const input = doc.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }
  return form;
};

const submitBuiltForm = (form: HTMLFormElement): void => {
  form.submit();
  // Safari defers form submission until after the current sync stack; removing
  // the form immediately can cancel the POST before it fires.
  window.setTimeout(() => {
    form.remove();
  }, 0);
};

const submitIntegrationFields = (
  action: string,
  fields: Record<string, string>,
  target = '_blank',
): void => {
  // The app shell form-action list blocks off-platform hosts (Sockpuppet).
  // Post to a same-origin handoff instead; that document's CSP allows the
  // one target origin and submits the same fields there.
  const selfOrigin = window.location.origin;
  const handoff = !integrationFormActionAllowed(action, selfOrigin)
    && externalIntegrationHandoffOrigin(action) !== null;
  const form = buildIntegrationForm(
    document,
    handoff ? INTEGRATION_HANDOFF_PATH : action,
    handoff ? { ...fields, [INTEGRATION_HANDOFF_ACTION_FIELD]: action } : fields,
    target,
  );
  document.body.appendChild(form);
  submitBuiltForm(form);
};

const submitIntegrationPost = (action: string, payload: IntegrationPostPayload): void => {
  submitIntegrationFields(action, integrationPostFields(action, payload));
};

const useIntegrationPresign = () => {
  const abortRef = useRef<AbortController | null>(null);

  /**
   * A flag `s3_url` under `/presigned/` is not a link. Fetch it from the
   * backend first, and only then POST `baseUrl`/`path` the same way as every
   * other integration, with the retrieved URL as `s3_url` and the other flag
   * params through the usual field rules. No tab is opened before that POST.
   */
  const start = (target: UploaderTarget): void => {
    const flagParams = target.config.params;
    const rawS3 = flagParams?.s3_url;
    const raw = typeof rawS3 === 'string' ? rawS3.trim() : '';
    if (!raw.startsWith('/presigned/')) {
      if (flagParams) {
        submitIntegrationFields(target.action, flagParamFields(flagParams, target.action, target.payload));
      } else {
        submitIntegrationPost(target.action, target.payload);
      }
      return;
    }

    const path = presignedIntegrationPath({ s3_url: raw });
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    if (!path) return;

    void (async () => {
      try {
        const s3Url = await requestIntegrationPresignedUrl(path, controller.signal);
        if (controller.signal.aborted) return;
        submitIntegrationFields(target.action, {
          ...flagParamFields(target.config.params, target.action, target.payload),
          s3_url: s3Url,
        });
      } catch (error) {
        if (controller.signal.aborted || (error instanceof Error && error.name === 'AbortError')) return;
      }
    })();
  };

  return { start };
};

export interface UploaderTarget {
  key: string;
  config: IntegrationConfig;
  action: string;
  payload: IntegrationPostPayload;
}

interface UseUploaderTargetsArgs {
  featureFlags?: { [key: string]: any };
  caseId?: string;
  tenantId?: string;
  username?: string;
  cellId?: string;
}

/**
 * Resolves configured third-party upload integrations into ready-to-submit POST
 * targets for the active case. Returns an empty array when integrations are
 * disabled, no valid case is selected, or nothing resolves. Shared between the
 * desktop dropdown and the mobile overflow modal so both stay in sync.
 */
export const useUploaderTargets = ({
  featureFlags,
  caseId,
  tenantId = '',
  username = '',
  cellId = '',
}: UseUploaderTargetsArgs): UploaderTarget[] => {
  const thirdPartyIntegrations = featureFlags?.thirdPartyIntegrations;
  const integrationItems = useMemo(
    () =>
      thirdPartyIntegrations?.enabled
        ? parseIntegrations(thirdPartyIntegrations.integrations)
        : [],
    [thirdPartyIntegrations?.enabled, thirdPartyIntegrations?.integrations],
  );

  const trimmedCaseId = caseId?.trim() ?? '';
  const isEmptyCase = trimmedCaseId === '';
  const isAllCasesSelected = trimmedCaseId === '*' || trimmedCaseId === 'All Cases';
  const personalFilesCaseId = usePersonalFilesCaseId();
  const isMyFilesCase = isAdHocCase(trimmedCaseId, personalFilesCaseId);

  const trimmedCellId = cellId?.trim() ?? '';

  return useMemo(() => {
    if (isEmptyCase || isAllCasesSelected || isMyFilesCase) return [];

    // Page-origin fallback for integrations with a ``"/"``/``""`` baseUrl, used
    // only when the cell id is unknown (see resolveCellFrontendOrigin). When we
    // have a cell id the target is the cell frontend host, so pageOrigin is
    // irrelevant.
    const pageOrigin =
      !trimmedCellId
      && integrationItems.some((item) => !item.config.baseUrl)
      && typeof window !== 'undefined'
        ? window.location.origin
        : undefined;

    return integrationItems
      .flatMap((item) => {
        const target = buildIntegrationPostTarget(
          item.config,
          tenantId,
          caseId ?? '',
          username,
          trimmedCellId || undefined,
          pageOrigin
        );
        return target ? [{ ...item, ...target }] : [];
      })
      .sort((a, b) => a.config.name.localeCompare(b.config.name));
  }, [integrationItems, tenantId, caseId, username, trimmedCellId, isEmptyCase, isAllCasesSelected, isMyFilesCase]);
};

interface UploadersMenuProps {
  featureFlags?: { [key: string]: any };
  caseId?: string;
  tenantId?: string;
  username?: string;
  cellId?: string;
  overflowLevel?: number;
  layout?: 'vertical' | 'horizontal';
  adHocFileUpload?: AdHocCaseFileUploadApi;
}

const UploadersMenu: React.FC<UploadersMenuProps> = ({
  featureFlags,
  caseId,
  tenantId = '',
  username = '',
  cellId = '',
  overflowLevel = 0,
  layout = 'vertical',
  adHocFileUpload,
}) => {
  const t = useTimePilotT();
  const statsContext = useContext(StatsContext);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const presign = useIntegrationPresign();

  const uploaderTargets = useUploaderTargets({ featureFlags, caseId, tenantId, username, cellId });
  const showAdHocFileUpload = Boolean(adHocFileUpload);
  const hasImportOptions = uploaderTargets.length > 0 || showAdHocFileUpload;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  if (!hasImportOptions) {
    return null;
  }

  const handleUploaderOpen = (target: UploaderTarget) => {
    presign.start(target);
    setIsOpen(false);
  };

  const handleAdHocUploadOpen = () => {
    adHocFileUpload?.openFilePicker();
    setIsOpen(false);
  };

  const menuItemCount = (showAdHocFileUpload ? 1 : 0) + uploaderTargets.length;

  const isHorizontal = layout === 'horizontal';

  return (
    <div ref={dropdownRef} className="relative flex items-center">
      <Button
        type="button"
        id="ImportButton"
        {...navBarButtonProps}
        size={isHorizontal ? 'sm' : undefined}
        aria-label={t('nav.import')}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={(event) => {
          setIsOpen((prev) => !prev);
          recordStat('UploadButtonClicked', event, statsContext);
        }}
        className={
          isHorizontal
            ? primaryNavActionClassName(isOpen)
            : navBarVerticalButtonClassName
        }
        leadingIcon={
          isHorizontal ? <UploadSimpleIcon size={16} weight="regular" /> : undefined
        }
      >
        {isHorizontal ? (
          t('nav.import')
        ) : (
          <span className={navBarVerticalStackClassName}>
            <span className={navBarVerticalIconClassName}>
              <UploadSimpleIcon size={28} weight="light" />
            </span>
            <span className={`whitespace-nowrap ${overflowLevel ? 'text-[18px]' : ''}`}>
              {t('nav.import')}
            </span>
          </span>
        )}
      </Button>
      {isOpen && (
        <div
          role="menu"
          aria-label={t('nav.import')}
          className="absolute top-full right-0 mt-2 bg-[#2C3A4A] rounded-md shadow-lg border border-white/10 z-[1001] min-w-[180px] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {showAdHocFileUpload && (
            <Button
              type="button"
              role="menuitem"
              style="text"
              color="light"
              background="inverse"
              size="sm"
              data-testid="AdHocFileUploadButton"
              aria-label={t('nav.uploadFiles')}
              disabled={adHocFileUpload!.isUploading || adHocFileUpload!.conflictModal.isOpen}
              onClick={handleAdHocUploadOpen}
              className={`${navBarMenuPanelItemClassName} disabled:opacity-50 ${
                menuItemCount === 1 ? 'rounded-md' : 'rounded-t-md'
              }`}
            >
              {adHocFileUpload!.isUploading ? t('nav.uploading') : t('nav.uploadFiles')}
            </Button>
          )}
          {uploaderTargets.map((target, index) => (
            <Button
              key={target.key}
              type="button"
              role="menuitem"
              style="text"
              color="light"
              background="inverse"
              size="sm"
              onClick={() => handleUploaderOpen(target)}
              className={`${navBarMenuPanelItemClassName} ${
                !showAdHocFileUpload && index === 0 ? 'rounded-t-md' : ''
              } ${index === uploaderTargets.length - 1 ? 'rounded-b-md' : ''}`}
            >
              {target.config.name}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
};

interface UploadersModalProps {
  targets: UploaderTarget[];
  onClose: () => void;
  adHocFileUpload?: AdHocCaseFileUploadApi;
}

/**
 * Centered, modal-style upload picker used on narrow viewports where the
 * desktop dropdown is collapsed into the overflow (ellipsis) menu. Rendered in a
 * portal so it overlays the page rather than being clipped by the nav bar.
 */
export const UploadersModal: React.FC<UploadersModalProps> = ({
  targets,
  onClose,
  adHocFileUpload,
}) => {
  const t = useTimePilotT();
  const [mounted, setMounted] = useState(false);
  const presign = useIntegrationPresign();
  const showAdHocFileUpload = Boolean(adHocFileUpload);
  const hasImportOptions = targets.length > 0 || showAdHocFileUpload;

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  useEffect(() => {
    if (!hasImportOptions) {
      onClose();
    }
  }, [hasImportOptions, onClose]);

  if (!mounted || !hasImportOptions) {
    return null;
  }

  const picker = createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('nav.import')}
      className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-[320px] bg-[#2C3A4A] rounded-lg shadow-xl border border-white/10 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div className="flex items-center gap-2 text-white">
            <UploadSimpleIcon size={20} weight="light" />
            <span className="text-base font-semibold">{t('nav.import')}</span>
          </div>
          <Button
            type="button"
            iconOnly
            size="sm"
            style="text"
            color="light"
            background="inverse"
            aria-label={t('nav.closeImport')}
            onClick={onClose}
            className="p-1 rounded-md text-white/80"
          >
            <XIcon />
          </Button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto py-1" role="menu" aria-label={t('nav.import')}>
          {showAdHocFileUpload && (
            <Button
              type="button"
              role="menuitem"
              style="text"
              color="light"
              background="inverse"
              size="sm"
              data-testid="AdHocFileUploadButton"
              aria-label={t('nav.uploadFiles')}
              disabled={adHocFileUpload!.isUploading || adHocFileUpload!.conflictModal.isOpen}
              onClick={() => {
                adHocFileUpload!.openFilePicker();
                onClose();
              }}
              className={`${navBarMenuPanelItemClassName} py-3 disabled:opacity-50`}
            >
              {adHocFileUpload!.isUploading ? t('nav.uploading') : t('nav.uploadFiles')}
            </Button>
          )}
          {targets.map((target) => (
            <Button
              key={target.key}
              type="button"
              role="menuitem"
              style="text"
              color="light"
              background="inverse"
              size="sm"
              onClick={() => {
                presign.start(target);
                onClose();
              }}
              className={`${navBarMenuPanelItemClassName} py-3`}
            >
              {target.config.name}
            </Button>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );

  return picker;
};

export default UploadersMenu;
